/**
 * /onboarding — Non-blocking 2-step profile completion wizard.
 *
 * Steps:
 *   1. Display name (required to proceed)
 *   2. Unified DIAN verification (skippable). One card handles
 *      identification + email together (no separate email step — the
 *      DIAN lookup returns the registered email and the OTP we send
 *      to that email proves both pieces in one shot).
 *
 * From step 2 the user can click "Completaré después" → /dashboard. The
 * ProfileCompletionBanner there will remind them to finish.
 */
import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { DianVerificationCard } from "@/components/profile/DianVerificationCard";
import {
  useProfile,
  useUpdateProfile,
} from "@/lib/api/profile.queries";
import type { ProfileIdentification } from "@/lib/api/profile";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
});

function OnboardingPage() {
  return (
    <RequireAuth>
      <OnboardingFlow />
    </RequireAuth>
  );
}

type Step = 1 | 2;

function OnboardingFlow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profileQuery = useProfile();
  const [step, setStep] = useState<Step>(1);

  if (profileQuery.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const profile = profileQuery.data?.profile;
  const completion = profileQuery.data?.completion;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-lg space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("profile.onboarding.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("profile.onboarding.subtitle")}
          </p>
        </div>

        {/* Progress */}
        {completion && (
          <div className="flex items-center gap-3">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${completion.score}%` }}
              />
            </div>
            <span className="text-xs tabular-nums text-muted-foreground">
              {completion.score}%
            </span>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex gap-2">
          {([1, 2] as Step[]).map((s) => (
            <div
              key={s}
              className={[
                "h-1.5 flex-1 rounded-full transition-colors",
                s < step ? "bg-primary" :
                s === step ? "bg-primary/60" : "bg-border",
              ].join(" ")}
            />
          ))}
        </div>

        {/* Step content */}
        {step === 1 && (
          <Step1DisplayNameCard
            initialName={profile?.displayName ?? ""}
            onSaved={() => {
              void profileQuery.refetch();
              setStep(2);
            }}
          />
        )}
        {step === 2 && (
          <Step2DianCard
            identification={
              profile?.identification ?? {
                type: null, number: null, dv: null,
                verified: false, verifiedAt: null, verifiedVia: null,
                legalNameDian: null, emailDian: null,
              }
            }
            onFinish={() => void navigate({ to: "/dashboard" })}
          />
        )}
      </div>
    </div>
  );
}

// ── Step 1: Display name ─────────────────────────────────────────────────────

function Step1DisplayNameCard({
  initialName,
  onSaved,
}: {
  initialName: string;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(initialName);
  const updateProfile = useUpdateProfile();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!displayName.trim()) return;
    try {
      await updateProfile.mutateAsync({ displayName: displayName.trim() });
      toast.success(t("profile.onboarding.step1_saved"));
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("profile.err_generic"));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.onboarding.step1_title")}</CardTitle>
        <CardDescription>{t("profile.onboarding.step1_subtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="onb-displayName">{t("profile.display_name.field")}</Label>
            <Input
              id="onb-displayName"
              required
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoFocus
              placeholder={t("profile.display_name.placeholder")}
            />
          </div>
          <Button type="submit" className="w-full" disabled={updateProfile.isPending || !displayName.trim()}>
            {updateProfile.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("profile.onboarding.step1_cta")}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Step 2: Unified DIAN verification ────────────────────────────────────────

function Step2DianCard({
  identification,
  onFinish,
}: {
  identification: ProfileIdentification;
  onFinish: () => void;
}) {
  const { t } = useTranslation();
  const profileQuery = useProfile();
  const currentIdentification =
    profileQuery.data?.profile?.identification ?? identification;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.onboarding.step2_title")}</CardTitle>
        <CardDescription>{t("profile.onboarding.step2_subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <DianVerificationCard current={currentIdentification} />
        <Separator />
        <div className="flex items-center justify-between">
          {currentIdentification.verified ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              {t("profile.verification.verified_badge", {
                via: currentIdentification.verifiedVia ?? "DIAN",
              })}
            </div>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onFinish}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {currentIdentification.verified
                ? t("profile.onboarding.finish")
                : t("profile.onboarding.skip")}
            </button>
            {currentIdentification.verified && (
              <Button size="sm" onClick={onFinish}>
                {t("profile.onboarding.go_dashboard")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
